import { Suspense, lazy, useEffect } from 'react'
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom'
import ScrollToTop from 'react-router-scroll-top'
import { useTranslation } from 'react-i18next'
import { getUserIP } from '@/apis/cloudfare'
import { ConfigProvider, notification } from 'antd'
import Theme from '@/containers/Theme'
import './i18n'
import './App.less'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import RoutePrivate from '@/containers/RoutePrivate'
import RoutePublic from '@/containers/RoutePublic'

import Loading from '@/components/Loading'
import ModalForceTasterOut from '@/components/ModalForceTasterOut'
import Error404 from '@/components/Error/404'
import useDirection from '@/hooks/useDirection'
import componentLoader from './utils/componentLoader'
import { useDispatch } from 'react-redux'
import { setIpCountry } from '@/store/users'

// import AnnouncementBar from './components/AnnouncementBar'

const Welcome = lazy(() => componentLoader(() => import('@/pages/Welcome')));
const GetStarted = lazy(() => componentLoader(() => import('@/pages/GetStarted')));
const FAQ = lazy(()=> componentLoader(() => import('@/pages/Faq')));
const Signup = lazy(() => componentLoader(() => import('@/pages/Signup')));
const SignupWalkin = lazy(() => componentLoader(() => import('@/pages/SignupWalkin')));
const Signin = lazy(() => componentLoader(() => import('@/pages/Signin')));
const SigninWithFacebook = lazy(() => componentLoader(() => import('@/pages/SigninWithFacebook')));
const SigninWithGoogle = lazy(() => componentLoader(() => import('@/pages/SigninWithGoogle')));
const SigninWithToken = lazy(() => componentLoader(() => import('@/pages/SigninWithToken')));
const ForgotPassword = lazy(() => componentLoader(() => import('@/pages/ForgotPassword')));
const ResetPassword = lazy(() => componentLoader(() => import('@/pages/ResetPassword')));
const ResendVerificationEmail = lazy(() => componentLoader(() => import('@/pages/ResendVerificationEmail')));
const VerifyEmail = lazy(() => componentLoader(() => import('@/pages/VerifyEmail')));
const ConnectWithSocial = lazy(() => componentLoader(() => import('@/pages/ConnectWithSocial')));
const TasterDashboard = lazy(() => componentLoader(() => import('@/pages/TasterDashboard')));
const Profile = lazy(() => componentLoader(() => import('@/pages/Profile')));
const Impersonate = lazy(() => componentLoader(() => import('@/pages/Impersonate')));
const SSOIdentify = lazy(() => componentLoader(() => import('@/pages/SSOIdentify')));
const Survey = lazy(() => componentLoader(() => import('@/pages/Survey')));
const ShareSurvey = lazy(() => componentLoader(() => import('@/pages/ShareSurvey')));
const QrRedirect = lazy(() => componentLoader(() => import('@/pages/QrRedirect')));
const NotVerified = lazy(() => componentLoader(() => import('@/pages/NotVerified')));
const MobileSignupOtp = lazy(() => componentLoader(() => import('@/pages/MobileSignupOtp')));
const MobileForgotPasswordOtp = lazy(() => componentLoader(() => import('@/pages/MobileForgotPasswordOtp')));
const MobileResetPassword = lazy(() => componentLoader(() => import('@/pages/MobileResetPassword')));
const Under18 = lazy(() => componentLoader(() => import('@/pages/Under18')));
const CodeExpired = lazy(() => componentLoader(() => import('@/pages/CodeExpired')));
const WalkinLanding = lazy(() => componentLoader(() => import('@/pages/WalkinLanding')));
const TwoFactorAuthentication = lazy(() => componentLoader(() => import('@/pages/TwoFactorAuthentication')));
const SelectBooking = lazy(() => componentLoader(() => import('@/pages/SelectBooking')));
const BookingLinkExpired = lazy(() => componentLoader(() => import('@/pages/BookingLinkExpired')));
const ConfirmBooking = lazy(() => componentLoader(() => import('@/pages/ConfirmBooking')));
const ConfirmSlot = lazy(() => componentLoader(() => import('@/pages/ConfirmSlot')));
const CancelBooking = lazy(() => componentLoader(() => import('@/pages/CancelBooking')));
const BookingCancelled = lazy(() => componentLoader(() => import('@/pages/BookingCancelled')));


const App = () => {
  const { t } = useTranslation()
  const [direction] = useDirection()
  const dispatch = useDispatch()

  useEffect(() => {
    getUserIP()
      .then((cloudfareData) => {
        if (cloudfareData?.loc) {
          dispatch(
            setIpCountry(String(cloudfareData.loc).trim().toUpperCase())
          )
          console.log('dev.src/App',{ ipCountry: String(cloudfareData.loc).trim().toUpperCase() })
        }
      })
      .catch(() => {})
  }, [dispatch])

  useEffect(() => {
    const splashScreen = document.querySelector('#splashscreen')
    if (splashScreen) {
      splashScreen.classList.add('hidden')
    }
  }, [])

  useEffect(() => {
    const onlineFunction = () => {
      notification.destroy()
      notification.success({
        message: t('internet.online'),
        placement: 'bottomLeft'
      })
    }

    const offlineFunction = () => {
      notification.destroy()
      notification.error({
        duration: 0,
        message: t('internet.offline'),
        placement: 'bottomLeft'
      })
    }

    window.addEventListener('online', onlineFunction)
    window.addEventListener('offline', offlineFunction)

    return () => {
      window.removeEventListener('online', onlineFunction)
      window.removeEventListener('offline', offlineFunction)
    }
  }, [])

  return (
    <ConfigProvider direction={direction}>
      <Router>
      <ScrollToTop>
        <Theme>
          {/* <AnnouncementBar text={t('announcementBar')}/> */}
          <Suspense fallback={<Loading />}>
            <Switch>
              <RoutePublic exact path='/'>
                <Welcome />
              </RoutePublic>

              <RoutePublic exact path='/get-started'>
                <GetStarted />
              </RoutePublic>

              <RoutePublic exact path='/faq'>
                <FAQ />
              </RoutePublic>
              
              <RoutePublic path='/sign-up'>
                <Signup />
              </RoutePublic>

              <RoutePublic path='/sign-up-walkin'>
                <SignupWalkin />
              </RoutePublic>

              <RoutePublic path='/sign-in'>
                <Signin />
              </RoutePublic>

              <RoutePublic path='/forgot-password'>
                <ForgotPassword />
              </RoutePublic>

              <RoutePublic path='/resend-verification-email'>
                <ResendVerificationEmail />
              </RoutePublic>

              <RoutePublic path='/onboarding/reset-password'>
                <ResetPassword />
              </RoutePublic>

              <RoutePublic path='/verify-email'>
                <VerifyEmail />
              </RoutePublic>

              <RoutePublic path='/requirement-information'>
                <ConnectWithSocial />
              </RoutePublic>

              <RoutePrivate path='/dashboard'>
                <TasterDashboard />
              </RoutePrivate>

              <RoutePrivate path='/profile'>
                <Profile />
              </RoutePrivate>

              <RoutePrivate path='/2fa'>
                <TwoFactorAuthentication />
              </RoutePrivate>

              <RoutePublic path='/walkin-landing/:surveyId'>
                <WalkinLanding />
              </RoutePublic>

              <Route path='/impersonate' component={Impersonate} />

              <Route path='/qr-code/:id' component={QrRedirect} />

              <Route path='/signin-with-token' component={SigninWithToken} />

              <Route path='/sso/identify' component={SSOIdentify} />

              <Route path='/signin-with-facebook' component={SigninWithFacebook}/>

              <Route path='/signin-with-google' component={SigninWithGoogle} />

              <Route path='/survey/:surveyId' component={Survey} />

              <Route path='/share/:uniqueName' component={ShareSurvey} />

              <Route path='/not-verified' component={NotVerified} />
              
              <RoutePublic path='/mobile-signup-otp'>
                <MobileSignupOtp />
              </RoutePublic>

              <RoutePublic path='/mobile-forgot-password-otp'>
                <MobileForgotPasswordOtp />
              </RoutePublic>

              <RoutePublic path='/mobile-reset-password'>
                <MobileResetPassword />
              </RoutePublic>

              <Route path='/under-18' component={Under18} />

              <Route path='/code-expired' component={CodeExpired} />

              <Route path='/cancel-booking/:bookingId/slot/:slotId' component={CancelBooking} />

              <Route path='/booking/:bookingId/slot/:slotId' component={ConfirmSlot} />
              
              <Route path='/booking/:bookingId' component={SelectBooking} />

              <Route path='/booking-confirmed' component={ConfirmBooking} />

              <Route path='/booking-link-expired' component={BookingLinkExpired} />

              <Route path='/booking-cancelled' component={BookingCancelled} />

              <Route path='*' component={Error404} />
            </Switch>
          </Suspense>

          <ModalForceTasterOut />
        </Theme>
      </ScrollToTop>
      </Router>
    </ConfigProvider>
  )
}

export default App
